import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  LinkSampleWrite,
  Project,
  ProjectSampleOrder,
  ProjectStatus,
  ProjectTransitionWrite,
  ProjectWrite,
  ProjectsResponse,
  SuggestedSamplesResponse,
} from './types'

const ROOT = '/api/projects'

export interface UseProjectsOptions {
  q?: string
  status?: ProjectStatus | null
  customerId?: number | null
  /** Sprint 10 — include soft-deleted rows (mirrors Formulations). */
  includeArchived?: boolean
}

export function useProjects(opts: UseProjectsOptions = {}) {
  const q = (opts.q ?? '').trim()
  const status = opts.status ?? null
  const customerId = opts.customerId ?? null
  const includeArchived = Boolean(opts.includeArchived)

  return useQuery({
    queryKey: ['projects', 'list', q, status, customerId, includeArchived],
    queryFn: () => {
      const search = new URLSearchParams()
      if (q) search.set('q', q)
      if (status) search.set('status', status)
      if (customerId !== null) search.set('customer_id', String(customerId))
      if (includeArchived) search.set('include_archived', '1')
      const qs = search.toString()
      return jsonFetch<ProjectsResponse>(qs ? `${ROOT}?${qs}` : ROOT)
    },
    staleTime: 15_000,
  })
}

export function useProject(id: number | null) {
  return useQuery({
    queryKey: ['projects', 'detail', id],
    queryFn: () => jsonFetch<Project>(`${ROOT}/${id}`),
    enabled: id !== null,
    staleTime: 15_000,
  })
}

export function useCreateProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ProjectWrite) =>
      jsonFetch<Project>(ROOT, { method: 'POST', body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects', 'list'] })
    },
  })
}

export interface UpdateProjectVars {
  id: number
  body: ProjectWrite
}

export function useUpdateProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: UpdateProjectVars) =>
      jsonFetch<Project>(`${ROOT}/${vars.id}`, {
        method: 'PUT',
        body: vars.body,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['projects', 'list'] })
      void qc.invalidateQueries({ queryKey: ['projects', 'detail', vars.id] })
    },
  })
}

export function useDeleteProject() {
  const qc = useQueryClient()
  return useMutation({
    // Soft-delete on the backend (sets deleted_at). Hook name + return
    // shape match the Formulations counterpart for consistency.
    mutationFn: (id: number) =>
      jsonFetch<{ ok: true; deleted_id: number; archived_at: string }>(
        `${ROOT}/${id}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects', 'list'] })
    },
  })
}

export function useRestoreProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      jsonFetch<Project>(`${ROOT}/${id}/restore`, { method: 'POST' }),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: ['projects', 'list'] })
      void qc.invalidateQueries({ queryKey: ['projects', 'detail', id] })
    },
  })
}

export interface TransitionProjectVars {
  id: number
  body: ProjectTransitionWrite
}

export function useTransitionProject() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: TransitionProjectVars) =>
      jsonFetch<Project>(`${ROOT}/${vars.id}/transition`, {
        method: 'POST',
        body: vars.body,
      }),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['projects', 'list'] })
      void qc.invalidateQueries({ queryKey: ['projects', 'detail', vars.id] })
    },
  })
}

// ─── Sample-order linkage (Chunk 4 will consume) ─────────────────────

export function useSuggestedSamples(projectId: number | null) {
  return useQuery({
    queryKey: ['projects', 'suggested-samples', projectId],
    queryFn: () =>
      jsonFetch<SuggestedSamplesResponse>(
        `${ROOT}/${projectId}/suggested-samples`,
      ),
    enabled: projectId !== null,
    staleTime: 30_000,
  })
}

export interface LinkSampleVars {
  projectId: number
  body: LinkSampleWrite
}

export function useLinkSample() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: LinkSampleVars) =>
      jsonFetch<{ ok: true; link: ProjectSampleOrder }>(
        `${ROOT}/${vars.projectId}/sample-orders`,
        { method: 'POST', body: vars.body },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['projects', 'detail', vars.projectId],
      })
      void qc.invalidateQueries({
        queryKey: ['projects', 'suggested-samples', vars.projectId],
      })
    },
  })
}

export interface UnlinkSampleVars {
  projectId: number
  sampleId: number
}

export function useUnlinkSample() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: UnlinkSampleVars) =>
      jsonFetch<{ ok: true; unlinked_id: number }>(
        `${ROOT}/${vars.projectId}/sample-orders/${vars.sampleId}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['projects', 'detail', vars.projectId],
      })
      void qc.invalidateQueries({
        queryKey: ['projects', 'suggested-samples', vars.projectId],
      })
    },
  })
}

// ─── Formulation linkage (Chunk 4 will consume) ─────────────────────

export interface LinkFormulationVars {
  projectId: number
  formulationId: number
}

export function useLinkFormulation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: LinkFormulationVars) =>
      jsonFetch<{ ok: true; formulation_id: number; project_id: number }>(
        `${ROOT}/${vars.projectId}/formulations/${vars.formulationId}`,
        { method: 'POST' },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['projects', 'detail', vars.projectId],
      })
      // Also bust formulations cache so the formulation now shows its
      // new project_id in any other consumer.
      void qc.invalidateQueries({ queryKey: ['formulations', 'list'] })
      void qc.invalidateQueries({
        queryKey: ['formulations', 'detail', vars.formulationId],
      })
    },
  })
}

export function useUnlinkFormulation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: LinkFormulationVars) =>
      jsonFetch<{ ok: true; formulation_id: number }>(
        `${ROOT}/${vars.projectId}/formulations/${vars.formulationId}`,
        { method: 'DELETE' },
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({
        queryKey: ['projects', 'detail', vars.projectId],
      })
      void qc.invalidateQueries({ queryKey: ['formulations', 'list'] })
      void qc.invalidateQueries({
        queryKey: ['formulations', 'detail', vars.formulationId],
      })
    },
  })
}
